import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';
import { addressFlag, moveLegacyStorage } from './legacy';
import { useMounted } from './prototype';

/**
 * «دبّه» is rolled out to ONE doctor profile: every visitor of that profile sees it, and no other profile
 * changes. The test tools stay with the owner (see useDabbehTester). Everything is decided on the client
 * (after mount), so server rendering and the shared caches of the profile stay exactly as they are
 * (docs/performance.md: withCSR stays user-independent).
 */
export const DABBEH_ROLLOUT_SLUGS: readonly string[] = ['دکتر-محمد-محمدی-125'];

/** Opening the profile once with `?dabbeh=1` (or `?dabbeh=0` to undo) marks this device as the owner's test device. */
const OPT_IN_KEY = 'dabbeh-opt-in';

const decode = (slug: string) => {
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
};

export const isDabbehRolloutSlug = (slug?: string | null) => !!slug && DABBEH_ROLLOUT_SLUGS.includes(decode(slug));

/**
 * Set once a seat of any account was seen on this device, and never cleared (unlike the seats themselves,
 * which leave on sign-out or an account switch): «نوبت‌های من» keeps looking for seats here, so a seat is
 * still found after signing in again. Holds no data, only that the queue was used.
 */
const USED_KEY = 'dabbeh-used';

export const markDabbehUsed = () => {
  try {
    window.localStorage.setItem(USED_KEY, '1');
  } catch {
    // storage blocked: the seats in the store still turn it on
  }
};

const readOptIn = () => {
  try {
    return window.localStorage.getItem(OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
};

const readUsed = () => {
  try {
    return window.localStorage.getItem(USED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Device opted in (see OPT_IN_KEY). With `onSlug`, the `?dabbeh=1|0` of the address is applied first —
 * and only on the rollout profile; on any other page the address is never read and nothing is written.
 */
export const useDabbehOptIn = (onSlug?: string | null) => {
  const [optedIn, setOptedIn] = useState(false);
  useEffect(() => {
    if (onSlug !== undefined && !isDabbehRolloutSlug(onSlug)) return;
    moveLegacyStorage();
    const param = addressFlag(window.location.search);
    try {
      if (onSlug !== undefined && param === '1') window.localStorage.setItem(OPT_IN_KEY, '1');
      if (onSlug !== undefined && param === '0') window.localStorage.removeItem(OPT_IN_KEY);
    } catch {
      // storage blocked: fall back to the account check only
    }
    setOptedIn(readOptIn());
  }, [onSlug]);
  return optedIn;
};

/**
 * Outside the profile («نوبت‌های من»): true only on a device that opted in, holds a seat in a rollout
 * doctor's queue, or ever did (USED_KEY). Read straight from localStorage so nobody else even downloads the
 * Dabbeh code.
 */
export const useDabbehOnDevice = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    moveLegacyStorage();
    // the queue's notification links here with ?dabbeh=1: the seat shows even on a device that never saw it
    if (addressFlag(window.location.search) === '1') {
      try {
        window.localStorage.setItem(OPT_IN_KEY, '1');
      } catch {
        // storage blocked: still on for this visit
      }
      setOn(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem('dabbeh-state');
      const slugs = Object.keys(JSON.parse(raw ?? '{}')?.state?.memberships ?? {});
      setOn(readOptIn() || readUsed() || slugs.some(isDabbehRolloutSlug));
    } catch {
      setOn(readOptIn() || readUsed());
    }
  }, []);
  return on;
};

/**
 * On a doctor profile: Dabbeh is on for every visitor of the rollout slug, and off everywhere else. Still
 * applies `?dabbeh=1|0` there (test device, see useDabbehTester). Decided after mount — never in SSR.
 */
export const useDabbehOnProfile = (slug?: string | null) => {
  useDabbehOptIn(slug ?? null);
  const mounted = useMounted();
  return mounted && isDabbehRolloutSlug(slug);
};

/**
 * The test tools are the owner's only: logged in with the doctor's own account (`doctorUserId` is the
 * profile's user id, kept on the seat), or a device opted in with `?dabbeh=1`. Other visitors never see them.
 */
export const useDabbehTester = (doctorUserId?: string | number | null) => {
  const optedIn = useDabbehOptIn();
  const userId = useUserInfoStore(state => state.info?.id);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const isOwner = isLogin && !!userId && doctorUserId != null && doctorUserId !== '' && String(doctorUserId) === String(userId);
  // once the owner's own account is recognised (on the profile), this device keeps the tools on every page
  // — also for seats joined before the seat carried the doctor's id
  useEffect(() => {
    if (!isOwner) return;
    try {
      window.localStorage.setItem(OPT_IN_KEY, '1');
    } catch {
      // storage blocked: the account check still works where the doctor's id is known
    }
  }, [isOwner]);
  return isOwner || optedIn;
};
